import React, { useCallback, useEffect, useState } from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import FormControlLabel from "@mui/material/FormControlLabel";
import Switch from "@mui/material/Switch";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import api from "../../../services/api";
import ComitesEditor from "./ComitesEditor";
import DirectorioEditor from "./DirectorioEditor";
import "../../../styles/SiteConfigPanel.css";

const getErrorMessage = (error, fallback) => {
  if (error?.response?.data) {
    if (typeof error.response.data === "string") {
      return error.response.data;
    }
    if (typeof error.response.data?.detail === "string") {
      return error.response.data.detail;
    }
  }
  return fallback;
};

const defaultConfig = {
  evento_link: "",
  directorio_visible: false,
  comites_visible: false,
  comites_json: [],
};

function SiteConfigPanel({ token }) {
  const [loading, setLoading] = useState(true);
  const [config, setConfig] = useState(defaultConfig);
  const [savingEvent, setSavingEvent] = useState(false);
  const [savingDirectorio, setSavingDirectorio] = useState(false);
  const [savingComites, setSavingComites] = useState(false);
  const [feedback, setFeedback] = useState(null);

  const authConfig = token
    ? { headers: { Authorization: `Bearer ${token}` } }
    : undefined;

  const loadConfig = useCallback(async () => {
    setLoading(true);
    setFeedback(null);

    try {
      const { data } = await api.get("/site-config/");
      setConfig({
        evento_link: data?.evento_link || "",
        directorio_visible: Boolean(data?.directorio_visible),
        comites_visible: Boolean(data?.comites_visible),
        comites_json: Array.isArray(data?.comites_json) ? data.comites_json : [],
      });
    } catch (error) {
      setFeedback({
        type: "error",
        message: getErrorMessage(error, "No se pudo cargar la configuración del sitio."),
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  const savePartialConfig = async (payload, successMessage) => {
    const { data } = await api.put("/site-config/", payload, authConfig);
    setConfig((prev) => ({
      ...prev,
      evento_link: data?.evento_link ?? prev.evento_link,
      directorio_visible:
        typeof data?.directorio_visible === "boolean"
          ? data.directorio_visible
          : prev.directorio_visible,
      comites_visible:
        typeof data?.comites_visible === "boolean"
          ? data.comites_visible
          : prev.comites_visible,
      comites_json: Array.isArray(data?.comites_json)
        ? data.comites_json
        : prev.comites_json,
    }));
    setFeedback({ type: "success", message: successMessage });
  };

  const handleSaveEvento = async () => {
    setSavingEvent(true);
    setFeedback(null);

    try {
      await savePartialConfig(
        { evento_link: config.evento_link.trim() },
        "Link del evento actualizado correctamente."
      );
    } catch (error) {
      setFeedback({
        type: "error",
        message: getErrorMessage(error, "No se pudo guardar el link del evento."),
      });
    } finally {
      setSavingEvent(false);
    }
  };

  const handleToggleDirectorio = async (event) => {
    const nextValue = event.target.checked;
    const previousValue = config.directorio_visible;
    setConfig((prev) => ({ ...prev, directorio_visible: nextValue }));
    setSavingDirectorio(true);
    setFeedback(null);

    try {
      await savePartialConfig(
        { directorio_visible: nextValue },
        "Visibilidad de Directorio actualizada."
      );
    } catch (error) {
      setConfig((prev) => ({ ...prev, directorio_visible: previousValue }));
      setFeedback({
        type: "error",
        message: getErrorMessage(error, "No se pudo actualizar la visibilidad de Directorio."),
      });
    } finally {
      setSavingDirectorio(false);
    }
  };

  const handleToggleComites = async (event) => {
    const nextValue = event.target.checked;
    const previousValue = config.comites_visible;
    setConfig((prev) => ({ ...prev, comites_visible: nextValue }));
    setSavingComites(true);
    setFeedback(null);

    try {
      await savePartialConfig(
        { comites_visible: nextValue },
        "Visibilidad de Comités Técnicos actualizada."
      );
    } catch (error) {
      setConfig((prev) => ({ ...prev, comites_visible: previousValue }));
      setFeedback({
        type: "error",
        message: getErrorMessage(error, "No se pudo actualizar la visibilidad de Comités Técnicos."),
      });
    } finally {
      setSavingComites(false);
    }
  };

  const handleSaveComitesJson = async (comitesArray) => {
    setFeedback(null);

    try {
      const { data } = await api.put(
        "/site-config/",
        { comites_json: comitesArray },
        authConfig
      );
      setConfig((prev) => ({
        ...prev,
        comites_json: Array.isArray(data?.comites_json)
          ? data.comites_json
          : comitesArray,
      }));
      setFeedback({
        type: "success",
        message: "Estructura de comités guardada correctamente.",
      });
    } catch (error) {
      setFeedback({
        type: "error",
        message: getErrorMessage(error, "No se pudo guardar la estructura de comités."),
      });
    }
  };

  if (loading) {
    return (
      <Box className="site-config-loading">
        <CircularProgress />
      </Box>
    );
  }

  const hasEventLink = Boolean(config.evento_link.trim());

  return (
    <section className="site-config-panel">
      <Typography variant="h5" className="site-config-title">
        Configuración del Sitio
      </Typography>

      {feedback && (
        <Alert severity={feedback.type} className="site-config-alert">
          {feedback.message}
        </Alert>
      )}

      <article className="site-config-card">
        <h3>Evento en vivo</h3>
        <TextField
          fullWidth
          label="Link del evento"
          value={config.evento_link}
          onChange={(event) =>
            setConfig((prev) => ({ ...prev, evento_link: event.target.value }))
          }
          placeholder="https://..."
        />
        <div className="site-config-actions">
          <Button
            variant="contained"
            onClick={handleSaveEvento}
            disabled={savingEvent}
          >
            {savingEvent ? "Guardando..." : "Guardar link"}
          </Button>
        </div>
        <p className={`site-config-status ${hasEventLink ? "is-active" : "is-inactive"}`}>
          {hasEventLink ? "Evento activo" : "Sin evento activo"}
        </p>
      </article>

      <article className="site-config-card">
        <h3>Visibilidad del Navbar</h3>
        <div className="site-config-switches">
          <FormControlLabel
            control={
              <Switch
                checked={config.directorio_visible}
                onChange={handleToggleDirectorio}
                disabled={savingDirectorio}
              />
            }
            label="Mostrar sección Directorio"
          />
          <FormControlLabel
            control={
              <Switch
                checked={config.comites_visible}
                onChange={handleToggleComites}
                disabled={savingComites}
              />
            }
            label="Mostrar sección Comités Técnicos"
          />
        </div>
      </article>

      <article className="site-config-card">
        <ComitesEditor
          value={config.comites_json || []}
          onSave={handleSaveComitesJson}
        />
      </article>

      <article className="site-config-card">
        <h3>Directorio</h3>
        <DirectorioEditor token={token} />
      </article>
    </section>
  );
}

export default SiteConfigPanel;
